import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Button,
  Input,
  Label,
  Spinner,
  Badge,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";
import classnames from "classnames";
import Flatpickr from "react-flatpickr";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getMonthlyOverviewReport } from "./_requests";
import ExcelJS from "exceljs";
import saveAs from "file-saver";

// Local calendar date (YYYY-MM-DD). toISOString() converts to UTC first, which in IST
// shifts any date picked before 05:30 local time back by one day.
const toLocalYMD = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const MonthlyOverviewReport: React.FC = () => {
  document.title = "Monthly Overview & Analytics | Paal Dappa Admin";

  const currentDate = new Date();
  const [filterType, setFilterType] = useState<"month_year" | "single_date" | "date_range">("month_year");
  const [selectedMonth, setSelectedMonth] = useState<number>(currentDate.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState<number>(currentDate.getFullYear());
  const [singleDate, setSingleDate] = useState<string>(toLocalYMD(currentDate));
  const [dateRange, setDateRange] = useState<Date[]>([]);

  const [loading, setLoading] = useState<boolean>(false);
  const [reportData, setReportData] = useState<any>(null);

  // Active Tab: 'users' | 'orders' | 'subscriptions'
  const [activeTab, setActiveTab] = useState<string>("users");
  const [showOverviewModal, setShowOverviewModal] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");

  const fetchReport = useCallback(() => {
    setLoading(true);
    let payload: any = {};

    if (filterType === "month_year") {
      payload = { month: selectedMonth, year: selectedYear };
    } else if (filterType === "single_date") {
      payload = { date: singleDate };
    } else if (filterType === "date_range" && dateRange.length === 2) {
      payload = {
        from_date: toLocalYMD(dateRange[0]),
        to_date: toLocalYMD(dateRange[1]),
      };
    }

    getMonthlyOverviewReport(payload)
      .then((res: any) => {
        if (res?.status) {
          setReportData(res.data);
        } else {
          toast.error(res?.message || "Failed to fetch overview report");
        }
      })
      .catch((err: any) => {
        console.error("Error fetching overview report:", err);
        toast.error("Error loading overview report data");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [filterType, selectedMonth, selectedYear, singleDate, dateRange]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleReset = () => {
    setFilterType("month_year");
    setSelectedMonth(currentDate.getMonth() + 1);
    setSelectedYear(currentDate.getFullYear());
    setSingleDate(toLocalYMD(currentDate));
    setDateRange([]);
    setSearchTerm("");
  };

  const months = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
  ];

  const years = [2024, 2025, 2026, 2027];

  const getPeriodLabel = () => {
    if (filterType === "month_year") {
      const monthObj = months.find((m) => m.value === selectedMonth);
      return `${monthObj?.label || selectedMonth}_${selectedYear}`;
    } else if (filterType === "single_date") {
      return singleDate;
    } else if (filterType === "date_range" && dateRange.length === 2) {
      return `${toLocalYMD(dateRange[0])}_to_${toLocalYMD(dateRange[1])}`;
    }
    return "Overview";
  };

  // Filtered lists based on search term
  const filteredUsers = useMemo(() => {
    if (!reportData?.new_customers_list) return [];
    if (!searchTerm.trim()) return reportData.new_customers_list;
    const term = searchTerm.toLowerCase();
    return reportData.new_customers_list.filter((u: any) =>
      (u.name || "").toLowerCase().includes(term) ||
      (u.email || "").toLowerCase().includes(term) ||
      (u.mobile_number || "").toLowerCase().includes(term) ||
      (u.customer_code || "").toLowerCase().includes(term)
    );
  }, [reportData, searchTerm]);

  const filteredOrders = useMemo(() => {
    if (!reportData?.orders_list) return [];
    if (!searchTerm.trim()) return reportData.orders_list;
    const term = searchTerm.toLowerCase();
    return reportData.orders_list.filter((o: any) =>
      (o.order_code || "").toLowerCase().includes(term) ||
      (o.customer_name || "").toLowerCase().includes(term) ||
      (o.customer_mobile || "").toLowerCase().includes(term) ||
      (o.payment_status || "").toLowerCase().includes(term) ||
      (o.order_status_name || "").toLowerCase().includes(term)
    );
  }, [reportData, searchTerm]);

  const filteredSubscriptions = useMemo(() => {
    if (!reportData?.subscriptions_list) return [];
    if (!searchTerm.trim()) return reportData.subscriptions_list;
    const term = searchTerm.toLowerCase();
    return reportData.subscriptions_list.filter((s: any) =>
      (s.plan_name || "").toLowerCase().includes(term) ||
      (s.customer_name || "").toLowerCase().includes(term) ||
      (s.customer_mobile || "").toLowerCase().includes(term) ||
      (s.payment_status || "").toLowerCase().includes(term)
    );
  }, [reportData, searchTerm]);

  const exportToExcel = async () => {
    if (!reportData) {
      toast.error("No report data available to export");
      return;
    }

    try {
      const periodLabel = getPeriodLabel();
      const workbook = new ExcelJS.Workbook();
      workbook.creator = "Paal Dappa Admin";
      workbook.created = new Date();

      // 1. Overview Summary Sheet
      const summarySheet = workbook.addWorksheet("Overview Summary");
      summarySheet.columns = [
        { header: "Metric / Indicator", key: "metric", width: 35 },
        { header: "Value / Details", key: "value", width: 45 },
      ];

      summarySheet.mergeCells("A1:B1");
      const titleCell = summarySheet.getCell("A1");
      titleCell.value = `Monthly Overview & Analytics Report (${periodLabel})`;
      titleCell.font = { bold: true, size: 14, color: { argb: "FFFFFFFF" } };
      titleCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF4B38B3" },
      };
      titleCell.alignment = { horizontal: "center", vertical: "middle" };
      summarySheet.getRow(1).height = 32;

      summarySheet.addRow([]);

      const headersRow = summarySheet.addRow(["Metric / Indicator", "Value / Details"]);
      headersRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
      headersRow.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF4B38B3" },
        };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });

      const metricsData = [
        ["Report Period", periodLabel],
        ["New Customer Registrations", reportData.new_customers_count || 0],
        ["Total Orders", reportData.total_orders || 0],
        ["New (1st Time) Orders", reportData.new_orders_count || 0],
        ["Total Subscriptions", reportData.total_subscriptions || 0],
        ["New (1st Time) Subscriptions", reportData.new_subscriptions_count || 0],
        [
          "Top Selling Product",
          reportData.highest_ordered_product
            ? `${reportData.highest_ordered_product.product_name} (${reportData.highest_ordered_product.total_quantity_ordered} units, ${reportData.highest_ordered_product.total_orders_count} orders)`
            : "N/A",
        ],
        [
          "Top Spender Customer",
          reportData.highest_bill_value_customer
            ? `${reportData.highest_bill_value_customer.name} - Code: ${reportData.highest_bill_value_customer.customer_code} (₹${reportData.highest_bill_value_customer.total_bill_value})`
            : "N/A",
        ],
      ];

      metricsData.forEach((row) => {
        const r = summarySheet.addRow(row);
        r.eachCell((cell) => {
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });
      });

      // 2. User Registrations Sheet
      const usersSheet = workbook.addWorksheet("User Registrations");
      usersSheet.columns = [
        { header: "S.No", key: "s_no", width: 8 },
        { header: "Customer Code", key: "customer_code", width: 18 },
        { header: "Name", key: "name", width: 25 },
        { header: "Email", key: "email", width: 30 },
        { header: "Mobile Number", key: "mobile_number", width: 18 },
        { header: "Registration Date", key: "created_at", width: 22 },
      ];

      const userHeaderRow = usersSheet.getRow(1);
      userHeaderRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
      userHeaderRow.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF4B38B3" },
        };
      });

      (reportData.new_customers_list || []).forEach((u: any, idx: number) => {
        const row = usersSheet.addRow({
          s_no: idx + 1,
          customer_code: u.customer_code || "N/A",
          name: u.name || "N/A",
          email: u.email || "N/A",
          mobile_number: u.mobile_number || "N/A",
          created_at: u.created_at ? new Date(u.created_at).toLocaleString() : "N/A",
        });
        row.eachCell((cell) => {
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });
      });

      // 3. Orders Sheet
      const ordersSheet = workbook.addWorksheet("Orders List");
      ordersSheet.columns = [
        { header: "S.No", key: "s_no", width: 8 },
        { header: "Order Code", key: "order_code", width: 20 },
        { header: "Customer Code", key: "customer_code", width: 16 },
        { header: "Customer Name", key: "customer_name", width: 25 },
        { header: "Mobile Number", key: "customer_mobile", width: 18 },
        { header: "Delivery Date", key: "delivery_date", width: 18 },
        { header: "Subtotal (₹)", key: "sub_total_amount", width: 15 },
        { header: "Tax (₹)", key: "tax_amount", width: 12 },
        { header: "Shipping (₹)", key: "shipping_charge", width: 14 },
        { header: "Discount (₹)", key: "discount_amount", width: 14 },
        { header: "Total Amount (₹)", key: "total_amount", width: 18 },
        { header: "Payment Method", key: "payment_method_name", width: 18 },
        { header: "Payment Status", key: "payment_status", width: 16 },
        { header: "Order Status", key: "order_status_name", width: 18 },
        { header: "Is First Order", key: "is_first_order", width: 14 },
        { header: "Created Date", key: "created_at", width: 22 },
      ];

      const orderHeaderRow = ordersSheet.getRow(1);
      orderHeaderRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
      orderHeaderRow.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF299CBD" },
        };
      });

      (reportData.orders_list || []).forEach((o: any, idx: number) => {
        const row = ordersSheet.addRow({
          s_no: idx + 1,
          order_code: o.order_code,
          customer_code: o.customer_code,
          customer_name: o.customer_name,
          customer_mobile: o.customer_mobile,
          delivery_date: o.delivery_date ? new Date(o.delivery_date).toLocaleDateString() : "N/A",
          sub_total_amount: o.sub_total_amount,
          tax_amount: o.tax_amount,
          shipping_charge: o.shipping_charge,
          discount_amount: o.discount_amount,
          total_amount: o.total_amount,
          payment_method_name: o.payment_method_name,
          payment_status: o.payment_status,
          order_status_name: o.order_status_name,
          is_first_order: o.is_first_order,
          created_at: o.created_at ? new Date(o.created_at).toLocaleString() : "N/A",
        });
        row.eachCell((cell) => {
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });
      });

      // 4. Subscriptions Sheet
      const subsSheet = workbook.addWorksheet("Subscriptions List");
      subsSheet.columns = [
        { header: "S.No", key: "s_no", width: 8 },
        { header: "Subscription ID", key: "id", width: 16 },
        { header: "Customer Code", key: "customer_code", width: 16 },
        { header: "Customer Name", key: "customer_name", width: 25 },
        { header: "Mobile Number", key: "customer_mobile", width: 18 },
        { header: "Plan Name", key: "plan_name", width: 22 },
        { header: "Start Date", key: "start_date", width: 16 },
        { header: "End Date", key: "end_date", width: 16 },
        { header: "Gross Amount (₹)", key: "gross_amount", width: 18 },
        { header: "Payment Status", key: "payment_status", width: 16 },
        { header: "Is First Subscription", key: "is_first_subscription", width: 20 },
        { header: "Created Date", key: "created_at", width: 22 },
      ];

      const subHeaderRow = subsSheet.getRow(1);
      subHeaderRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
      subHeaderRow.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF0AB39C" },
        };
      });

      (reportData.subscriptions_list || []).forEach((s: any, idx: number) => {
        const row = subsSheet.addRow({
          s_no: idx + 1,
          id: `SUB-${s.id}`,
          customer_code: s.customer_code,
          customer_name: s.customer_name,
          customer_mobile: s.customer_mobile,
          plan_name: s.plan_name,
          start_date: s.start_date ? new Date(s.start_date).toLocaleDateString() : "N/A",
          end_date: s.end_date ? new Date(s.end_date).toLocaleDateString() : "N/A",
          gross_amount: s.gross_amount,
          payment_status: s.payment_status,
          is_first_subscription: s.is_first_subscription,
          created_at: s.created_at ? new Date(s.created_at).toLocaleString() : "N/A",
        });
        row.eachCell((cell) => {
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const cleanLabel = periodLabel.replace(/[^a-zA-Z0-9_-]/g, "_");
      saveAs(blob, `Monthly_Report_${cleanLabel}.xlsx`);
      toast.success("Excel report downloaded successfully");
    } catch (err: any) {
      console.error("Error exporting to Excel:", err);
      toast.error("Failed to generate Excel report");
    }
  };

  const exportToCSV = () => {
    if (!reportData) {
      toast.error("No report data available to export");
      return;
    }

    try {
      const periodLabel = getPeriodLabel();
      const rows = [
        ["Monthly Overview & Analytics Report"],
        ["Period", periodLabel],
        [""],
        ["Metric / Indicator", "Value / Details"],
        ["New Customer Registrations", reportData.new_customers_count || 0],
        ["Total Orders", reportData.total_orders || 0],
        ["New (1st Time) Orders", reportData.new_orders_count || 0],
        ["Total Subscriptions", reportData.total_subscriptions || 0],
        ["New (1st Time) Subscriptions", reportData.new_subscriptions_count || 0],
        [
          "Top Selling Product",
          reportData.highest_ordered_product
            ? `${reportData.highest_ordered_product.product_name} (${reportData.highest_ordered_product.total_quantity_ordered} units, ${reportData.highest_ordered_product.total_orders_count} orders)`
            : "N/A",
        ],
        [
          "Top Spender Customer",
          reportData.highest_bill_value_customer
            ? `${reportData.highest_bill_value_customer.name} - Code: ${reportData.highest_bill_value_customer.customer_code} (Rs.${reportData.highest_bill_value_customer.total_bill_value})`
            : "N/A",
        ],
      ];

      const csvContent =
        "data:text/csv;charset=utf-8," +
        rows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")).join("\n");

      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      const cleanLabel = periodLabel.replace(/[^a-zA-Z0-9_-]/g, "_");
      link.setAttribute("download", `Monthly_Report_Summary_${cleanLabel}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("CSV summary report downloaded successfully");
    } catch (err: any) {
      console.error("Error exporting CSV:", err);
      toast.error("Failed to generate CSV report");
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Monthly Report Lists & Analytics" pageTitle="Reports" />
          <ToastContainer />

          {/* Filter Bar Card */}
          <Row>
            <Col lg={12}>
              <Card className="shadow-sm border-0 mb-4">
                <CardHeader className="bg-light-subtle border-bottom border-light">
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <h5 className="card-title mb-0 fw-bold">
                      <i className="ri-filter-3-line align-bottom me-2 text-primary"></i>
                      Report Period Filters
                    </h5>
                    <div className="d-flex gap-2 flex-wrap">
                      <Button color="primary" size="sm" onClick={fetchReport} disabled={loading}>
                        {loading ? <Spinner size="sm" className="me-1" /> : <i className="ri-search-line me-1"></i>}
                        Fetch Report
                      </Button>
                      <Button color="soft-secondary" size="sm" onClick={handleReset} disabled={loading}>
                        <i className="ri-refresh-line me-1"></i> Reset
                      </Button>
                      <Button
                        color="info"
                        size="sm"
                        onClick={() => setShowOverviewModal(true)}
                        disabled={loading || !reportData}
                      >
                        <i className="ri-bar-chart-box-line me-1"></i> Overview Summary
                      </Button>
                      <UncontrolledDropdown>
                        <DropdownToggle color="success" size="sm" disabled={loading || !reportData}>
                          <i className="ri-download-2-line me-1"></i> Download Report <i className="ri-arrow-down-s-line ms-1"></i>
                        </DropdownToggle>
                        <DropdownMenu end>
                          <DropdownItem onClick={exportToExcel}>
                            <i className="ri-file-excel-2-line text-success me-2 align-middle fs-16"></i>
                            Download Excel Workbook (.xlsx)
                          </DropdownItem>
                          <DropdownItem onClick={exportToCSV}>
                            <i className="ri-file-text-line text-primary me-2 align-middle fs-16"></i>
                            Download CSV Summary (.csv)
                          </DropdownItem>
                        </DropdownMenu>
                      </UncontrolledDropdown>
                    </div>
                  </div>
                </CardHeader>

                <CardBody>
                  <Row className="g-3 align-items-end">
                    <Col md={3} sm={6}>
                      <Label className="form-label fw-semibold">Filter Mode</Label>
                      <Input
                        type="select"
                        value={filterType}
                        onChange={(e) => setFilterType(e.target.value as any)}
                        className="form-select"
                      >
                        <option value="month_year">Month & Year</option>
                        <option value="single_date">Particular Date</option>
                        <option value="date_range">Custom Date Range</option>
                      </Input>
                    </Col>

                    {filterType === "month_year" && (
                      <>
                        <Col md={3} sm={6}>
                          <Label className="form-label fw-semibold">Select Month</Label>
                          <Input
                            type="select"
                            value={selectedMonth}
                            onChange={(e) => setSelectedMonth(Number(e.target.value))}
                            className="form-select"
                          >
                            {months.map((m) => (
                              <option key={m.value} value={m.value}>
                                {m.label}
                              </option>
                            ))}
                          </Input>
                        </Col>
                        <Col md={3} sm={6}>
                          <Label className="form-label fw-semibold">Select Year</Label>
                          <Input
                            type="select"
                            value={selectedYear}
                            onChange={(e) => setSelectedYear(Number(e.target.value))}
                            className="form-select"
                          >
                            {years.map((y) => (
                              <option key={y} value={y}>
                                {y}
                              </option>
                            ))}
                          </Input>
                        </Col>
                      </>
                    )}

                    {filterType === "single_date" && (
                      <Col md={4} sm={6}>
                        <Label className="form-label fw-semibold">Select Date</Label>
                        <Input
                          type="date"
                          value={singleDate}
                          onChange={(e) => setSingleDate(e.target.value)}
                          className="form-control"
                        />
                      </Col>
                    )}

                    {filterType === "date_range" && (
                      <Col md={5} sm={12}>
                        <Label className="form-label fw-semibold">Select Date Range</Label>
                        <Flatpickr
                          options={{
                            mode: "range",
                            dateFormat: "Y-m-d",
                          }}
                          className="form-control"
                          placeholder="Select From - To Date"
                          onChange={(dates: Date[]) => setDateRange(dates)}
                        />
                      </Col>
                    )}
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Loader */}
          {loading && (
            <Row className="my-5 py-5 text-center">
              <Col>
                <Spinner color="primary" style={{ width: "3rem", height: "3rem" }} />
                <p className="mt-3 text-muted fw-semibold">Loading report lists...</p>
              </Col>
            </Row>
          )}

          {/* Main Report Lists Section */}
          {!loading && reportData && (
            <Row>
              <Col lg={12}>
                <Card className="shadow-sm border-0 mb-4">
                  <CardHeader className="bg-light-subtle border-bottom border-light p-0">
                    <div className="d-flex justify-content-between align-items-center flex-wrap px-3 pt-3">
                      {/* Nav Tabs */}
                      <Nav tabs className="nav-tabs-custom card-header-tabs border-bottom-0">
                        <NavItem>
                          <NavLink
                            className={classnames({ active: activeTab === "users" }, "fw-semibold cursor-pointer")}
                            onClick={() => setActiveTab("users")}
                            style={{ cursor: "pointer" }}
                          >
                            <i className="ri-user-add-line me-1 text-primary align-middle"></i>
                            User Registrations
                            <Badge color="primary" pill className="ms-2">
                              {reportData.new_customers_count || 0}
                            </Badge>
                          </NavLink>
                        </NavItem>
                        <NavItem>
                          <NavLink
                            className={classnames({ active: activeTab === "orders" }, "fw-semibold cursor-pointer")}
                            onClick={() => setActiveTab("orders")}
                            style={{ cursor: "pointer" }}
                          >
                            <i className="ri-shopping-bag-3-line me-1 text-info align-middle"></i>
                            Orders List
                            <Badge color="info" pill className="ms-2">
                              {reportData.total_orders || 0}
                            </Badge>
                          </NavLink>
                        </NavItem>
                        <NavItem>
                          <NavLink
                            className={classnames({ active: activeTab === "subscriptions" }, "fw-semibold cursor-pointer")}
                            onClick={() => setActiveTab("subscriptions")}
                            style={{ cursor: "pointer" }}
                          >
                            <i className="ri-repeat-line me-1 text-success align-middle"></i>
                            Subscriptions List
                            <Badge color="success" pill className="ms-2">
                              {reportData.total_subscriptions || 0}
                            </Badge>
                          </NavLink>
                        </NavItem>
                      </Nav>

                      {/* Search Input for Current Tab */}
                      <div className="pb-3" style={{ minWidth: "240px" }}>
                        <div className="search-box">
                          <Input
                            type="text"
                            placeholder="Search in list..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="form-control search"
                          />
                        </div>
                      </div>
                    </div>
                  </CardHeader>

                  <CardBody>
                    <TabContent activeTab={activeTab}>
                      {/* 1. User Registrations Tab */}
                      <TabPane tabId="users">
                        <div className="table-responsive table-card">
                          <Table className="align-middle table-nowrap mb-0 table-hover">
                            <thead className="table-light text-muted">
                              <tr>
                                <th scope="col" style={{ width: "60px" }}>S.No</th>
                                <th scope="col">Customer Code</th>
                                <th scope="col">Name</th>
                                <th scope="col">Email</th>
                                <th scope="col">Mobile Number</th>
                                <th scope="col">Registration Date</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredUsers.length > 0 ? (
                                filteredUsers.map((item: any, index: number) => (
                                  <tr key={item.id || index}>
                                    <td className="fw-medium">{index + 1}</td>
                                    <td>
                                      <span className="badge bg-light text-dark border">
                                        {item.customer_code || `CUST-${item.id}`}
                                      </span>
                                    </td>
                                    <td className="fw-semibold text-primary">{item.name || "N/A"}</td>
                                    <td>{item.email || "N/A"}</td>
                                    <td>{item.mobile_number || "N/A"}</td>
                                    <td>
                                      {item.created_at ? new Date(item.created_at).toLocaleString() : "N/A"}
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan={6} className="text-center py-4 text-muted">
                                    <i className="ri-user-unfollow-line fs-36 d-block mb-2 text-muted"></i>
                                    No customer registrations found for this period.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </Table>
                        </div>
                      </TabPane>

                      {/* 2. Orders Tab */}
                      <TabPane tabId="orders">
                        <div className="table-responsive table-card">
                          <Table className="align-middle table-nowrap mb-0 table-hover">
                            <thead className="table-light text-muted">
                              <tr>
                                <th scope="col" style={{ width: "60px" }}>S.No</th>
                                <th scope="col">Order Code</th>
                                <th scope="col">Customer Name</th>
                                <th scope="col">Mobile Number</th>
                                <th scope="col">Delivery Date</th>
                                <th scope="col">Total Amount</th>
                                <th scope="col">Payment Status</th>
                                <th scope="col">Order Status</th>
                                <th scope="col">1st Order?</th>
                                <th scope="col">Created Date</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredOrders.length > 0 ? (
                                filteredOrders.map((item: any, index: number) => (
                                  <tr key={item.id || index}>
                                    <td className="fw-medium">{index + 1}</td>
                                    <td className="fw-bold text-dark">{item.order_code}</td>
                                    <td className="fw-semibold">{item.customer_name}</td>
                                    <td>{item.customer_mobile}</td>
                                    <td>
                                      {item.delivery_date ? new Date(item.delivery_date).toLocaleDateString() : "N/A"}
                                    </td>
                                    <td className="fw-bold text-success">
                                      ₹{Number(item.total_amount || 0).toLocaleString("en-IN")}
                                    </td>
                                    <td>
                                      <Badge
                                        color={
                                          item.payment_status?.toLowerCase() === "success" ||
                                          item.payment_status?.toLowerCase() === "completed" ||
                                          item.payment_status?.toLowerCase() === "paid"
                                            ? "success"
                                            : "warning"
                                        }
                                        className="text-uppercase"
                                      >
                                        {item.payment_status}
                                      </Badge>
                                    </td>
                                    <td>
                                      <span className="badge text-uppercase bg-info-subtle text-info">
                                        {item.order_status_name}
                                      </span>
                                    </td>
                                    <td>
                                      {item.is_first_order === "Yes" ? (
                                        <Badge color="warning" className="px-2 py-1">
                                          ✨ 1st Order
                                        </Badge>
                                      ) : (
                                        <span className="text-muted fs-12">Repeat</span>
                                      )}
                                    </td>
                                    <td>
                                      {item.created_at ? new Date(item.created_at).toLocaleString() : "N/A"}
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan={10} className="text-center py-4 text-muted">
                                    <i className="ri-inbox-line fs-36 d-block mb-2 text-muted"></i>
                                    No orders found for this period.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </Table>
                        </div>
                      </TabPane>

                      {/* 3. Subscriptions Tab */}
                      <TabPane tabId="subscriptions">
                        <div className="table-responsive table-card">
                          <Table className="align-middle table-nowrap mb-0 table-hover">
                            <thead className="table-light text-muted">
                              <tr>
                                <th scope="col" style={{ width: "60px" }}>S.No</th>
                                <th scope="col">Subscription ID</th>
                                <th scope="col">Customer Name</th>
                                <th scope="col">Mobile Number</th>
                                <th scope="col">Plan Name</th>
                                <th scope="col">Start Date</th>
                                <th scope="col">End Date</th>
                                <th scope="col">Gross Amount</th>
                                <th scope="col">Payment Status</th>
                                <th scope="col">1st Sub?</th>
                                <th scope="col">Created Date</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredSubscriptions.length > 0 ? (
                                filteredSubscriptions.map((item: any, index: number) => (
                                  <tr key={item.id || index}>
                                    <td className="fw-medium">{index + 1}</td>
                                    <td className="fw-bold text-dark">SUB-{item.id}</td>
                                    <td className="fw-semibold">{item.customer_name}</td>
                                    <td>{item.customer_mobile}</td>
                                    <td className="fw-semibold text-primary">{item.plan_name}</td>
                                    <td>
                                      {item.start_date ? new Date(item.start_date).toLocaleDateString() : "N/A"}
                                    </td>
                                    <td>
                                      {item.end_date ? new Date(item.end_date).toLocaleDateString() : "N/A"}
                                    </td>
                                    <td className="fw-bold text-success">
                                      ₹{Number(item.gross_amount || 0).toLocaleString("en-IN")}
                                    </td>
                                    <td>
                                      <Badge
                                        color={
                                          item.payment_status?.toLowerCase() === "success" ||
                                          item.payment_status?.toLowerCase() === "completed" ||
                                          item.payment_status?.toLowerCase() === "paid"
                                            ? "success"
                                            : "warning"
                                        }
                                        className="text-uppercase"
                                      >
                                        {item.payment_status}
                                      </Badge>
                                    </td>
                                    <td>
                                      {item.is_first_subscription === "Yes" ? (
                                        <Badge color="warning" className="px-2 py-1">
                                          ✨ 1st Sub
                                        </Badge>
                                      ) : (
                                        <span className="text-muted fs-12">Renewal</span>
                                      )}
                                    </td>
                                    <td>
                                      {item.created_at ? new Date(item.created_at).toLocaleString() : "N/A"}
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan={11} className="text-center py-4 text-muted">
                                    <i className="ri-repeat-line fs-36 d-block mb-2 text-muted"></i>
                                    No subscriptions found for this period.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </Table>
                        </div>
                      </TabPane>
                    </TabContent>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}

          {/* Overview Summary Modal */}
          <Modal
            isOpen={showOverviewModal}
            toggle={() => setShowOverviewModal(!showOverviewModal)}
            size="lg"
            centered={true}
          >
            <ModalHeader toggle={() => setShowOverviewModal(false)} className="bg-light border-bottom">
              <span className="fw-bold fs-16 text-primary">
                <i className="ri-bar-chart-box-line me-2"></i>
                Overview Analytics ({getPeriodLabel()})
              </span>
            </ModalHeader>
            <ModalBody className="p-4">
              {reportData && (
                <>
                  {/* Metric Cards Row */}
                  <Row className="g-3">
                    {/* New Customers */}
                    <Col sm={6} xl={3}>
                      <Card className="border-0 shadow-sm bg-primary bg-gradient text-white mb-0">
                        <CardBody className="p-3">
                          <div className="d-flex align-items-center">
                            <div className="avatar-sm flex-shrink-0">
                              <span className="avatar-title bg-white bg-opacity-25 rounded-circle fs-4 text-white">
                                <i className="ri-user-add-line"></i>
                              </span>
                            </div>
                            <div className="flex-grow-1 ms-3">
                              <p className="text-uppercase fw-medium text-white-50 mb-1 fs-11">New Customers</p>
                              <h4 className="fs-20 fw-bold text-white mb-0">{reportData.new_customers_count || 0}</h4>
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    </Col>

                    {/* Total Orders */}
                    <Col sm={6} xl={3}>
                      <Card className="border-0 shadow-sm bg-info bg-gradient text-white mb-0">
                        <CardBody className="p-3">
                          <div className="d-flex align-items-center">
                            <div className="avatar-sm flex-shrink-0">
                              <span className="avatar-title bg-white bg-opacity-25 rounded-circle fs-4 text-white">
                                <i className="ri-shopping-bag-3-line"></i>
                              </span>
                            </div>
                            <div className="flex-grow-1 ms-3">
                              <p className="text-uppercase fw-medium text-white-50 mb-1 fs-11">Total Orders</p>
                              <h4 className="fs-20 fw-bold text-white mb-0">{reportData.total_orders || 0}</h4>
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    </Col>

                    {/* Total Subscriptions */}
                    <Col sm={6} xl={3}>
                      <Card className="border-0 shadow-sm bg-success bg-gradient text-white mb-0">
                        <CardBody className="p-3">
                          <div className="d-flex align-items-center">
                            <div className="avatar-sm flex-shrink-0">
                              <span className="avatar-title bg-white bg-opacity-25 rounded-circle fs-4 text-white">
                                <i className="ri-repeat-line"></i>
                              </span>
                            </div>
                            <div className="flex-grow-1 ms-3">
                              <p className="text-uppercase fw-medium text-white-50 mb-1 fs-11">Total Subscriptions</p>
                              <h4 className="fs-20 fw-bold text-white mb-0">{reportData.total_subscriptions || 0}</h4>
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    </Col>

                    {/* First Time Orders / Subscriptions */}
                    <Col sm={6} xl={3}>
                      <Card className="border-0 shadow-sm bg-warning bg-gradient text-white mb-0">
                        <CardBody className="p-3">
                          <div className="d-flex align-items-center">
                            <div className="avatar-sm flex-shrink-0">
                              <span className="avatar-title bg-white bg-opacity-25 rounded-circle fs-4 text-white">
                                <i className="ri-sparkles-line"></i>
                              </span>
                            </div>
                            <div className="flex-grow-1 ms-3">
                              <p className="text-uppercase fw-medium text-white-50 mb-1 fs-11">New 1st Orders / Subs</p>
                              <h4 className="fs-20 fw-bold text-white mb-0">
                                {reportData.new_orders_count || 0} / {reportData.new_subscriptions_count || 0}
                              </h4>
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>

                  {/* Highlights Section */}
                  <Row className="g-3 mt-2">
                    {/* Highest Ordered Product */}
                    <Col md={6}>
                      <Card className="shadow-sm border mb-0 h-100">
                        <CardHeader className="bg-light border-bottom p-3">
                          <div className="d-flex align-items-center">
                            <i className="ri-trophy-line me-2 text-warning fs-18"></i>
                            <h6 className="card-title mb-0 fw-bold fs-14">Highest Ordered Product</h6>
                          </div>
                        </CardHeader>
                        <CardBody className="p-3">
                          {reportData.highest_ordered_product ? (
                            <div className="p-2 bg-light rounded border">
                              <h6 className="fw-bold text-primary mb-1">
                                {reportData.highest_ordered_product.product_name}
                              </h6>
                              <small className="text-muted d-block mb-2">
                                ID: #{reportData.highest_ordered_product.product_id}
                                {reportData.highest_ordered_product.product_unique_id &&
                                  ` | Code: ${reportData.highest_ordered_product.product_unique_id}`}
                              </small>
                              <Row className="g-2 text-center">
                                <Col sm={6}>
                                  <div className="p-2 bg-white rounded border">
                                    <span className="text-muted fs-11 d-block">Quantity Ordered</span>
                                    <span className="fs-15 fw-bold text-dark">
                                      {reportData.highest_ordered_product.total_quantity_ordered} units
                                    </span>
                                  </div>
                                </Col>
                                <Col sm={6}>
                                  <div className="p-2 bg-white rounded border">
                                    <span className="text-muted fs-11 d-block">Orders Count</span>
                                    <span className="fs-15 fw-bold text-dark">
                                      {reportData.highest_ordered_product.total_orders_count} orders
                                    </span>
                                  </div>
                                </Col>
                              </Row>
                            </div>
                          ) : (
                            <div className="text-center py-3 text-muted fs-13">
                              No product order data available.
                            </div>
                          )}
                        </CardBody>
                      </Card>
                    </Col>

                    {/* Customer with Highest Bill Value */}
                    <Col md={6}>
                      <Card className="shadow-sm border mb-0 h-100">
                        <CardHeader className="bg-light border-bottom p-3">
                          <div className="d-flex align-items-center">
                            <i className="ri-vip-crown-2-line me-2 text-warning fs-18"></i>
                            <h6 className="card-title mb-0 fw-bold fs-14">Highest Bill Value Customer</h6>
                          </div>
                        </CardHeader>
                        <CardBody className="p-3">
                          {reportData.highest_bill_value_customer ? (
                            <div className="p-2 bg-light rounded border">
                              <h6 className="fw-bold text-dark mb-1">
                                {reportData.highest_bill_value_customer.name}
                              </h6>
                              <small className="text-muted d-block mb-2">
                                Code: {reportData.highest_bill_value_customer.customer_code} | 📞 {reportData.highest_bill_value_customer.mobile_number}
                              </small>
                              <div className="p-2 bg-white rounded border text-center">
                                <span className="text-muted fs-11 d-block">Total Spend / Bill Value</span>
                                <span className="fs-16 fw-bold text-success">
                                  ₹{Number(reportData.highest_bill_value_customer.total_bill_value).toLocaleString("en-IN")}
                                </span>
                              </div>
                            </div>
                          ) : (
                            <div className="text-center py-3 text-muted fs-13">
                              No customer billing data available.
                            </div>
                          )}
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                </>
              )}
            </ModalBody>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default MonthlyOverviewReport;
